import {
  flip,
  FloatingPortal,
  offset,
  shift,
  size as sizeFloatingUI,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'
import cn from 'classnames'
import { FC, ReactNode, useCallback, useEffect, useState } from 'react'
import styles from './Popup.module.scss'

interface PopupProps {
  className?: string
  isOpen: boolean
  setIsOpen: (value: boolean) => void
  size?: 'large' | 'medium' | 'small'
  buttonChildren: ReactNode
  popupChildren: ReactNode
}
export const Popup: FC<PopupProps> = ({
  className,
  isOpen,
  setIsOpen,
  size = 'large',
  buttonChildren,
  popupChildren,
}) => {
  const { refs, floatingStyles, update, context } = useFloating<HTMLDivElement>(
    {
      open: isOpen,
      onOpenChange: setIsOpen,
      placement: 'bottom-start',
      middleware: [
        offset(8),
        flip(),
        shift({ padding: 8 }),
        sizeFloatingUI({
          apply({ rects, elements }) {
            Object.assign(elements.floating.style, {
              width: `${rects.reference.width}px`, // Устанавливаем ширину попапа равной ширине кнопки
            })
          },
        }),
      ],
    }
  )

  const dismiss = useDismiss(context, {
    outsidePress: true,
    referencePress: false,
    escapeKey: true,
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss])

  useEffect(() => {
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [update])

  const toggleOpen = useCallback(() => {
    update()
    setIsOpen(!isOpen)
  }, [isOpen, setIsOpen, update])

  return (
    <>
      <div
        className={cn(styles.container, className)}
        ref={(ref) => refs.setReference(ref)}
        {...getReferenceProps({
          onClick: toggleOpen,
        })}
      >
        {buttonChildren}
      </div>
      <FloatingPortal>
        <div
          ref={(ref) => refs.setFloating(ref)}
          style={floatingStyles}
          className={cn(styles.popup, {
            [styles.isHidden]: !isOpen,
            [styles.isSmall]: size === 'small',
          })}
          {...getFloatingProps()}
        >
          {popupChildren}
        </div>
      </FloatingPortal>
    </>
  )
}
